<template>
    <div class="flex w-full flex-col">
        <form v-if="step === 'email'" class="flex flex-col" @submit.prevent="onEmailFormSubmit">
            <!--
                The intro stands down while a keyboard is up. It is guidance read before typing, and
                on a phone the keyboard leaves the panel about 270px: with this paragraph in it the
                SSO buttons at the bottom of the form were cut off by the panel's own edge.
            -->
            <p v-if="!isKeyboardOpen" class="text-sm text-black/60">
                {{
                    emailIsTaken
                        ? $t('pages.product_detail_page.ai_upload.auth.login.context', { email: email ?? '' })
                        : $t('pages.product_detail_page.ai_upload.auth.email.context')
                }}
            </p>

            <!--
                Not autofocused on a phone: the keyboard would come up over a panel the customer has
                not read yet and take half the screen with it, hiding the sign-in alternatives below
                the form — SSO among them — before they were ever seen. Tapping the field is what
                asks for it.

                Not disabled while the request is in flight either, only the button is. Disabling a
                field that has focus blurs it, and on iOS a blur closes the keyboard for good: the
                code step that follows focuses its own field, and a focus outside a user gesture
                cannot reopen the keyboard there. So the customer got the code screen with no
                keyboard and had to tap the field, while on Android the same focus opens it. Leaving
                the field alone keeps the keyboard up across the step, and moving focus between text
                fields while it is up needs no gesture. Re-submitting is the button's own business.
            -->
            <Input
                v-model="emailHttp.email"
                name="email"
                type="email"
                :class="isKeyboardOpen ? 'mt-3' : 'mt-4'"
                :placeholder="$t('pages.product_detail_page.ai_upload.auth.email.placeholder')"
                autocomplete="email"
                :autofocus="isTabletUp"
                :error="emailHttp.errors.email ?? registrationHttp.errors.email"
                :disabled="emailIsTaken"
            />

            <!--
                Spacing above this content lives inside the collapsible (pt-4) rather
                than as a margin/gap on the collapsible's own box from the parent.
                A margin from the parent stays fixed while the box's measured height
                animates to 0, leaving a lingering gap that then vanishes in one frame
                the moment the box unmounts — a visible "hard cut" right at the end.
                Padding inside the measured content animates smoothly with everything else.
            -->
            <Collapsible :open="emailIsTaken">
                <div class="space-y-4 pt-4">
                    <PasswordInput
                        ref="passwordInputRef"
                        v-model="loginHttp.password"
                        v-model:visible="showPassword"
                        name="password"
                        :placeholder="$t('pages.product_detail_page.ai_upload.auth.login.password_placeholder')"
                        :error="loginHttp.errors.password"
                        :disabled="loginHttp.processing || !emailIsTaken"
                    />

                    <p v-if="loginGeneralError" class="text-sm font-medium text-red-600">
                        {{ $t('pages.product_detail_page.ai_upload.auth.login.general_error') }}
                    </p>

                    <div class="flex justify-end">
                        <Link :href="forgotPasswordRoute().url" class="text-sm text-primary underline transition hover:opacity-80">
                            {{ $t('pages.product_detail_page.ai_upload.auth.login.forgot_password') }}
                        </Link>
                    </div>
                </div>
            </Collapsible>

            <p v-if="emailGeneralError" class="mt-4 text-sm font-medium text-red-600">
                {{ $t('pages.product_detail_page.ai_upload.auth.email.error') }}
            </p>

            <Button
                type="submit"
                task="next"
                :class="isKeyboardOpen ? 'mt-3 h-14 w-full text-base font-extrabold' : 'mt-4 h-14 w-full text-base font-extrabold'"
                :label="
                    emailIsTaken
                        ? $t('pages.product_detail_page.ai_upload.auth.login.submit')
                        : $t('pages.product_detail_page.ai_upload.auth.email.continue')
                "
                :loading="emailHttp.processing || loginHttp.processing || registrationHttp.processing"
                :disabled="emailHttp.processing || loginHttp.processing || registrationHttp.processing"
            />
        </form>

        <form v-else-if="step === 'otp'" class="space-y-4" @submit.prevent="submitOtp">
            <p v-if="!isKeyboardOpen" class="text-sm text-black/60">
                {{ $t('pages.product_detail_page.ai_upload.auth.otp.intro', { email: email ?? '' }) }}
            </p>

            <PinInput
                ref="otpInputRef"
                v-model="otpHttp.otp"
                name="otp"
                :length="6"
                :error="otpHttp.errors.otp"
                :disabled="otpHttp.processing"
                autofocus
            />

            <p v-if="otpGeneralError" class="text-sm font-medium text-red-600" v-text="otpGeneralError" />

            <p v-if="otpResendMessage" class="text-sm font-medium text-order-buttons">
                {{ $t('pages.product_detail_page.ai_upload.auth.otp.resend_sent') }}
            </p>

            <!--
                h-14/text-base: the same size as the order button the whole AI flow is driven by.
                This one is reached on a phone, one hand, mid-checkout — it was a 44px target with
                the keyboard still up.
            -->
            <button
                type="submit"
                class="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-x-1.5 rounded-md bg-order-buttons px-3.5 text-base font-extrabold text-white transition hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-75"
                :disabled="otpHttp.processing"
            >
                <Spinner v-if="otpHttp.processing" size="sm" for-button />
                {{ $t('pages.product_detail_page.ai_upload.auth.otp.submit_continue') }}
            </button>

            <button
                type="button"
                class="w-full cursor-pointer text-center text-sm text-primary underline transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isResending"
                @click="resendOtp"
            >
                {{ $t('pages.product_detail_page.ai_upload.auth.otp.resend') }}
            </button>
        </form>
    </div>
</template>

<script setup lang="ts">
import { useAppearanceComposable } from '@/composables/appearance/AppearanceComposable';
import { useKeyboardInset } from '@/composables/useKeyboardInset';
import Collapsible from '@/components/collapsible/Collapsible.vue';
import Spinner from '@/components/spinners/Spinner.vue';
import Button from '@/components/buttons/Button.vue';
import Input from '@/components/forms/inputs/Input.vue';
import PasswordInput from '@/components/forms/inputs/PasswordInput.vue';
import PinInput from '@/components/forms/inputs/PinInput.vue';
import I18n from '@/i18n';
import CheckEmailAvailabilityController from '@/types/wayfinder/actions/App/Http/Controllers/Web/Customer/CheckEmailAvailabilityController';
import StartAiCustomerRegistrationController from '@/types/wayfinder/actions/App/Http/Controllers/Web/AiProducts/StartAiCustomerRegistrationController';
import VerifyAiCustomerOtpController from '@/types/wayfinder/actions/App/Http/Controllers/Web/AiProducts/VerifyAiCustomerOtpController';
import AuthenticateLoginController from '@/types/wayfinder/actions/App/Http/Controllers/Web/Customer/Auth/Login/AuthenticateLoginController';
import { request as forgotPasswordRoute } from '@/types/wayfinder/routes/customer/password';
import type { AppPageProps } from '@/types';
import { trackLogin, trackSignUp } from '@/composables/useAuthTracking';
import { Link, useHttp, usePage } from '@inertiajs/vue3';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

export type AiAuthStep = 'email' | 'login' | 'otp';

type AiAuthPageProps = AppPageProps & { ai_otp_registration_enabled?: boolean };

type EmailForm = { email: string | undefined };
type EmailAvailabilityResponse = { available: boolean };

type LoginForm = { email: string | undefined; password: string | undefined };

type RegistrationForm = { email: string | undefined };

/**
 * A registration_token means a code was emailed and is waiting to be entered. Its absence means
 * the store has the code step switched off, so that same request created the account and signed
 * the customer in — nothing is left to confirm.
 */
type RegistrationStartedResponse = { registration_token?: string; authenticated?: boolean };

type OtpForm = { registration_token: string | undefined; otp: string | undefined };

const emits = defineEmits<{ success: []; 'step-change': [step: AiAuthStep]; 'photo-picker-requested': [] }>();

const { keyboardInset } = useKeyboardInset();
const { isTabletUp } = useAppearanceComposable();

const page = usePage<AiAuthPageProps>();

/** Store setting from Sytze Sync, off unless switched on — see AiRegistrationConfigService. */
const isOtpEnabled = computed<boolean>(() => page.props.ai_otp_registration_enabled ?? false);

/** Above zero means the on-screen keyboard is taking part of the panel's room. */
const isKeyboardOpen = computed<boolean>(() => keyboardInset.value > 0);

const step = ref<'email' | 'otp'>('email');
const emailIsTaken = ref(false);
const email = ref<string | undefined>(undefined);
const registrationToken = ref<string | undefined>(undefined);
const showPassword = ref(false);
const isResending = ref(false);
const emailGeneralError = ref(false);
const loginGeneralError = ref(false);
const otpGeneralError = ref<string | undefined>(undefined);
const otpResendMessage = ref(false);
const passwordInputRef = useTemplateRef('passwordInputRef');
const otpInputRef = useTemplateRef('otpInputRef');

// The password field appears inline underneath the email field rather than as
// its own step — this computed exists only so the modal title (which keys off
// 'email' | 'login' | 'otp') still reflects that phase without a real step.
const displayStep = computed<AiAuthStep>(() => {
    return step.value === 'email' && emailIsTaken.value ? 'login' : step.value;
});

watch(displayStep, value => emits('step-change', value), { immediate: true });

// The field is disabled (and so unfocusable) until emailIsTaken flips — wait
// for that DOM update to flush before focusing, otherwise focus() is called
// on a still-disabled input and silently does nothing.
watch(emailIsTaken, async taken => {
    if (!taken) {
        return;
    }

    await nextTick();
    passwordInputRef.value?.focus();
});

// The OTP form only mounts once the modal is already open (the email step
// switches to it via v-else-if) — the browser's native autofocus attribute
// isn't reliable for that case here, so focus explicitly.
watch(step, async value => {
    if (value !== 'otp') {
        return;
    }

    await nextTick();
    otpInputRef.value?.focus();
});

const emailHttp = useHttp<EmailForm, EmailAvailabilityResponse>({ email: undefined });
const loginHttp = useHttp<LoginForm>({ email: undefined, password: undefined });
const registrationHttp = useHttp<RegistrationForm, RegistrationStartedResponse>({ email: undefined });
const otpHttp = useHttp<OtpForm>({ registration_token: undefined, otp: undefined });

/**
 * Asks the page for the photo picker from inside the tap that submits this form.
 *
 * iOS only opens a picker from a live user gesture, and the sign-in round trip outlives it — which
 * is why signing in used to end with the customer back on the page, hunting for the upload button
 * to press a second time. Asked for here instead, the picker is up before the request has even
 * answered, and the sign-in finishes behind it.
 *
 * Only from the tap that actually completes the sign-in, so the customer is never looking at a
 * picker with a step still waiting underneath it: the email tap when there is no code step, the
 * code tap when there is. An existing email is the one case this cannot know in advance — the
 * password field appears behind the picker, and the photo is kept for the login that follows.
 *
 * Synchronous all the way down to open(), or the gesture is gone before the picker is asked for:
 * everything from here to el.click() runs inside this same event.
 */
function requestPhotoPicker(): void {
    emits('photo-picker-requested');
}

async function onEmailFormSubmit(): Promise<void> {
    if (emailIsTaken.value) {
        if (filled(loginHttp.password) && !loginHttp.processing) {
            requestPhotoPicker();
        }

        await submitLogin();

        return;
    }

    // With a code step to come this tap does not finish anything — the code tap asks instead.
    if (!isOtpEnabled.value && filled(emailHttp.email) && !emailHttp.processing) {
        requestPhotoPicker();
    }

    await submitEmail();
}

/** An empty field submits nothing, and the picker would only cover the one still to fill. */
function filled(value: string | undefined): boolean {
    return value !== undefined && value.trim().length > 0;
}

async function submitEmail(): Promise<void> {
    if (emailHttp.processing) {
        return;
    }

    emailGeneralError.value = false;

    try {
        await emailHttp.post(CheckEmailAvailabilityController().url, {
            onSuccess: data => onEmailChecked(data.available),
            onError: errors => {
                if (Object.keys(errors).length === 0) {
                    emailGeneralError.value = true;
                }
            },
            onHttpException: () => {
                emailGeneralError.value = true;
            },
            onNetworkError: () => {
                emailGeneralError.value = true;
            },
        });
    } catch {
        // Errors are handled by the callbacks above.
    }
}

function onEmailChecked(available: boolean): void {
    email.value = emailHttp.email;

    if (!available) {
        loginHttp.email = emailHttp.email;
        emailIsTaken.value = true;
        return;
    }

    // Optimistic: move to the OTP step immediately rather than waiting for
    // startRegistration()'s own round trip (it sends two emails server-side,
    // which is noticeably slower) — the availability check already confirms
    // this email is new, which is all the OTP screen needs to render.
    //
    // Not with the code step switched off: there is no code screen to show, and
    // the same request creates the account and signs the customer in. The form
    // stays where it is, loading, until it answers.
    if (isOtpEnabled.value) {
        step.value = 'otp';
    }

    startRegistration();
}

async function startRegistration(): Promise<void> {
    registrationHttp.email = email.value;

    try {
        await registrationHttp.post(StartAiCustomerRegistrationController().url, {
            onSuccess: data => onRegistrationStarted(data),
            onError: errors => {
                onRegistrationFailed(Object.keys(errors).length > 0);
            },
            onHttpException: () => {
                onRegistrationFailed(false);
            },
            onNetworkError: () => {
                onRegistrationFailed(false);
            },
        });
    } catch {
        // Errors are handled by the callbacks above.
    }
}

/**
 * The response decides which step follows, not isOtpEnabled: that prop is baked into a page the
 * CDN may hold for a week, so it can describe the store as it was configured back then. A token
 * means a code is waiting; without one the account exists and the customer is already signed in.
 */
function onRegistrationStarted(data: RegistrationStartedResponse): void {
    if (data.registration_token === undefined) {
        trackSignUp('email');
        emits('success');
        return;
    }

    registrationToken.value = data.registration_token;
    otpHttp.registration_token = data.registration_token;
    step.value = 'otp';
}

function onRegistrationFailed(hasFieldErrors: boolean): void {
    step.value = 'email';

    // A field error already renders under the email input — the general line would repeat it.
    emailGeneralError.value = !hasFieldErrors;
}

async function submitLogin(): Promise<void> {
    if (loginHttp.processing) {
        return;
    }

    loginGeneralError.value = false;

    try {
        await loginHttp.post(AuthenticateLoginController().url, {
            onSuccess: () => {
                trackLogin('email');
                emits('success');
            },
            onError: errors => {
                if (Object.keys(errors).length === 0) {
                    loginGeneralError.value = true;
                }
            },
            onHttpException: () => {
                loginGeneralError.value = true;
            },
            onNetworkError: () => {
                loginGeneralError.value = true;
            },
        });
    } catch {
        // Errors are handled by the callbacks above.
    }
}

async function submitOtp(): Promise<void> {
    if (otpHttp.processing) {
        return;
    }

    if (filled(otpHttp.otp)) {
        requestPhotoPicker();
    }

    otpGeneralError.value = undefined;
    otpResendMessage.value = false;

    try {
        await otpHttp.post(VerifyAiCustomerOtpController().url, {
            onSuccess: () => {
                trackSignUp('otp');
                emits('success');
            },
            onError: errors => {
                if (Object.keys(errors).length === 0) {
                    otpGeneralError.value = otpGenericErrorMessage();
                }
            },
            onHttpException: response => {
                otpGeneralError.value = extractExceptionMessage(response.data) ?? otpGenericErrorMessage();
            },
            onNetworkError: () => {
                otpGeneralError.value = otpGenericErrorMessage();
            },
        });
    } catch {
        // Errors are handled by the callbacks above.
    }
}

async function resendOtp(): Promise<void> {
    if (isResending.value) {
        return;
    }

    isResending.value = true;
    otpResendMessage.value = false;

    try {
        await registrationHttp.post(StartAiCustomerRegistrationController().url, {
            onSuccess: data => {
                // The store switched the code step off while this screen was open: rather than
                // a new code, that request created the account and signed the customer in, so
                // there is nothing left to wait for here.
                if (data.registration_token === undefined) {
                    trackSignUp('email');
                    emits('success');
                    return;
                }

                registrationToken.value = data.registration_token;
                otpHttp.registration_token = data.registration_token;
                otpHttp.otp = undefined;
                otpResendMessage.value = true;
            },
            onError: () => {
                otpGeneralError.value = otpGenericErrorMessage();
            },
            onHttpException: () => {
                otpGeneralError.value = otpGenericErrorMessage();
            },
            onNetworkError: () => {
                otpGeneralError.value = otpGenericErrorMessage();
            },
        });
    } finally {
        isResending.value = false;
    }
}

function otpGenericErrorMessage(): string {
    return I18n.global.t('pages.product_detail_page.ai_upload.auth.otp.general_error');
}

function extractExceptionMessage(data: string | Record<string, unknown>): string | undefined {
    const parsed = typeof data === 'string' ? safeJsonParse(data) : data;
    const message = parsed?.message;

    return typeof message === 'string' ? message : undefined;
}

function safeJsonParse(value: string): Record<string, unknown> | undefined {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
}
</script>
